import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { signIn as amplifySignIn } from 'aws-amplify/auth';
import { LockKeyholeIcon, MailIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import z from 'zod';

import googleIcon from '@assets/google-icon.svg';
import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { Button } from '@components/ui/button';
import { Separator } from '@components/ui/separator';
import {
  resolvePostSignInRedirect,
  startGoogleSignIn,
} from '@helpers/ssoSignIn.helper';
import { cn } from '@lib/utils';

const redirectUrlSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute('/_auth-layout/')({
  component: SignIn,
  validateSearch: search => redirectUrlSchema.parse(search),
});

const formSchema = z.object({
  email: z
    .email()
    .min(1, 'Email is required and must be valid (e.g. example@email.eu)'),
  password: z.string().min(1, 'Password is required'),
});

function SignIn() {
  const navigate = Route.useNavigate();
  const { redirect } = Route.useSearch();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const { isPending, mutate: signIn } = useMutation({
    mutationFn: amplifySignIn,
    onError: () => {
      toast.error('Warning', {
        description: 'Invalid credentials. Please try again.',
      });
    },
    onSuccess: () => {
      navigate({
        to: resolvePostSignInRedirect({ redirect }),
        replace: true,
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    signIn({
      username: values.email,
      password: values.password,
    });
  });

  return (
    <Form.Provider {...form}>
      <form
        className={cn('mt-12 grid w-full gap-2 transition-all duration-200', {
          'space-y-4': !!form.formState.errors.email,
        })}
        onSubmit={handleSubmit}
      >
        <InputControlled
          icon={MailIcon}
          name="email"
          placeholder="example@email.eu"
          size="xl"
        />
        <InputControlled
          autoComplete="off"
          icon={LockKeyholeIcon}
          name="password"
          placeholder="******"
          secure
          size="xl"
          type="password"
        />

        {/* <Anchor
          className="text-secondary ml-auto font-normal"
          to="/forgot-password"
        >
          Forgot password?
        </Anchor> */}

        <Button
          className="mx-auto mt-12 w-4/6"
          isLoading={isPending}
          size="lg"
          type="submit"
          variant="secondary"
        >
          Login
        </Button>

        {import.meta.env.VITE_CONNECT_GOOGLE_SIGN_IN_ENABLED === 'true' && (
          <>
            <div className="my-4 flex items-center gap-x-12">
              <Separator variant="auth" />
              <span className="text-sm text-gray-400">or</span>
              <Separator variant="auth" />
            </div>
            <Button
              className="mx-auto w-4/6 font-semibold"
              disabled={isPending}
              onClick={() => startGoogleSignIn(redirect)}
              size="lg"
              type="button"
              variant="google"
            >
              <img alt="Google icon" className="mr-2 size-6" src={googleIcon} />
              Continue with Google
            </Button>
          </>
        )}
      </form>
    </Form.Provider>
  );
}
